import { useEffect, useState, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import {
  FiPlusCircle,
  FiZap,
  FiActivity,
  FiCheckCircle,
  FiSend,
  FiClock,
  FiTrendingUp,
  FiClipboard,
  FiCompass,
  FiAlertOctagon,
} from "react-icons/fi";
import { Truck, Rocket, Inbox, Sparkles } from "lucide-react";
import MissionCard from "./components/MissionCard";
import {
  listMissions,
  listMyProposals,
  listMyMissions,
  formatPrice,
} from "../../utils/missionsApi";

export default function MissionsDashboard() {
  const navigate = useNavigate();
  const ctx = useOutletContext() || {};
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const prenom = (localStorage.getItem("prenom") || "").trim();
  const isProvider = role === "partner" || role === "admin";
  const canCreateMission =
    role === "admin" ||
    (role === "pro" && localStorage.getItem("proCanPostMissions") === "true");

  const [recent, setRecent] = useState([]);
  const [urgent, setUrgent] = useState([]);
  const [myCreated, setMyCreated] = useState([]);
  const [myActive, setMyActive] = useState([]);
  const [myProposals, setMyProposals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rec, urg, mine, active, proposals] = await Promise.all([
        listMissions({ limit: 6, sort: "-createdAt", status: "ouverte,propositions_recues" }).catch(() => ({ items: [] })),
        listMissions({ limit: 6, urgent: true, status: "ouverte,propositions_recues" }).catch(() => ({ items: [] })),
        listMyMissions("created").catch(() => ({ items: [] })),
        listMyMissions("assigned").catch(() => ({ items: [] })),
        isProvider ? listMyProposals().catch(() => ({ items: [] })) : Promise.resolve({ items: [] }),
      ]);
      setRecent(rec.items || []);
      setUrgent(urg.items || []);
      setMyCreated(mine.items || []);
      setMyActive(active.items || []);
      setMyProposals(proposals.items || []);
    } finally {
      setLoading(false);
    }
  }, [isProvider]);

  useEffect(() => { load(); }, [load]);

  const s = ctx.stats || {};

  const kpis = [
    { variant: "brand", icon: <FiActivity />, label: "Missions actives", value: s.activeAsClient ?? myCreated.filter((m) => ["ouverte","propositions_recues","proposition_acceptee","en_cours"].includes(m.status)).length },
    isProvider && { variant: "success", icon: <FiCheckCircle />, label: "Acceptées", value: s.activeAsProvider ?? myActive.length },
    isProvider && { variant: "info", icon: <FiSend />, label: "Candidatures en attente", value: s.myProposals ?? myProposals.filter((p) => ["envoyee","vue"].includes(p.status)).length },
    isProvider && { variant: "violet", icon: <FiTrendingUp />, label: "Revenus estimés", value: formatPrice(s.estimatedEarnings || 0), raw: true },
    { variant: "danger", icon: <FiZap />, label: "Urgences à proximité", value: s.urgentNearby ?? urgent.length },
    { variant: "warn", icon: <FiClock />, label: "À valider", value: s.pendingValidation ?? 0 },
  ].filter(Boolean);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      {/* ============== HERO ============== */}
      <section className="pmm-hero">
        <div className="pmm-hero__inner">
          <div className="pmm-hero__icon">
            <Truck size={32} strokeWidth={2.2} />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <h1 className="pmm-hero__title">
              {prenom ? `Bonjour ${prenom}, ` : "Bienvenue sur "}ProMeet Missions
            </h1>
            <p className="pmm-hero__sub">
              {isProvider
                ? "Trouvez des missions automobiles près de chez vous, postulez en 30 secondes et suivez vos chantiers en temps réel."
                : "Postez vos missions automobiles, puis notre équipe valide l'annonce et l'attribution des prestataires."}
            </p>
          </div>
          <div className="pmm-hero__cta">
            <button className="pmm-btn" onClick={() => navigate("/missions/list")}>
              <FiCompass /> Parcourir
            </button>
            {canCreateMission && (
              <button className="pmm-btn pmm-btn--primary" onClick={() => navigate("/missions/create")}>
                <Rocket size={16} /> Poster une mission
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="pmm-topbar">
        <div>
          <h1 className="pmm-h-accent">Tableau de bord</h1>
          <div className="pmm-topbar-sub">
            Vos missions, candidatures et urgences en un coup d'œil.
          </div>
        </div>
        <div className="pmm-topbar-actions">
          <button className="pmm-btn pmm-btn--ghost" onClick={() => navigate("/missions/list")}>
            <FiClipboard /> Toutes les missions
          </button>
        </div>
      </div>

      <div className="pmm-grid pmm-grid--4">
        {kpis.map((k, idx) => (
          <div className={`pmm-kpi pmm-kpi--${k.variant}`} key={idx} style={{ animationDelay: `${idx * 50}ms` }}>
            <div className="pmm-kpi-icon">{k.icon}</div>
            <div className="pmm-kpi-meta">
              <span className="label">{k.label}</span>
              <span className="value">{k.raw ? k.value : (k.value ?? 0)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ============== URGENT ============== */}
      <section className="pmm-card">
        <div className="pmm-section-header pmm-section-header--fire">
          <h2>
            <span className="pmm-section-header__icon"><FiAlertOctagon /></span>
            Missions urgentes
          </h2>
          <button className="pmm-btn pmm-btn--ghost" onClick={() => navigate("/missions/urgent")}>Tout voir</button>
        </div>
        {loading ? <div className="pmm-spinner" />
          : urgent.length === 0 ? (
            <div className="pmm-empty">
              <div className="pmm-empty-icon"><FiZap size={28} /></div>
              <h3>Aucune mission urgente</h3>
              <p>Les nouvelles missions urgentes apparaîtront ici en temps réel.</p>
            </div>
          ) : (
            <div className="pmm-grid pmm-grid--2">
              {urgent.slice(0, 4).map((m) => (
                <MissionCard
                  key={m._id}
                  mission={m}
                  onOpen={() => navigate(`/missions/${m._id}`)}
                  onApply={isProvider ? () => navigate(`/missions/${m._id}?apply=1`) : undefined}
                />
              ))}
            </div>
          )}
      </section>

      {/* ============== RECENT ============== */}
      <section className="pmm-card">
        <div className="pmm-section-header pmm-section-header--info">
          <h2>
            <span className="pmm-section-header__icon"><Sparkles size={18} /></span>
            Nouvelles missions
          </h2>
          <button className="pmm-btn pmm-btn--ghost" onClick={() => navigate("/missions/list")}>Tout voir</button>
        </div>
        {loading ? <div className="pmm-spinner" />
          : recent.length === 0 ? (
            <div className="pmm-empty">
              <div className="pmm-empty-icon"><Inbox size={28} /></div>
              <h3>Pas encore de mission</h3>
              <p>Postez la première mission ou attendez de nouvelles offres.</p>
              <div style={{ marginTop: 12 }}>
                {canCreateMission && (
                  <button className="pmm-btn pmm-btn--primary" onClick={() => navigate("/missions/create")}>
                    <FiPlusCircle /> Créer une mission
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="pmm-grid pmm-grid--2">
              {recent.slice(0, 6).map((m) => (
                <MissionCard
                  key={m._id}
                  mission={m}
                  onOpen={() => navigate(`/missions/${m._id}`)}
                  onApply={isProvider ? () => navigate(`/missions/${m._id}?apply=1`) : undefined}
                />
              ))}
            </div>
          )}
      </section>

      {/* ============== ACTIVE (Provider) ============== */}
      {isProvider && myActive.length > 0 && (
        <section className="pmm-card">
          <div className="pmm-section-header pmm-section-header--violet">
            <h2>
              <span className="pmm-section-header__icon"><FiActivity /></span>
              Mes missions actives
            </h2>
            <button className="pmm-btn pmm-btn--ghost" onClick={() => navigate("/missions/accepted")}>Tout voir</button>
          </div>
          <div className="pmm-grid pmm-grid--2">
            {myActive.slice(0, 4).map((m) => (
              <MissionCard
                key={m._id}
                mission={m}
                onOpen={() => navigate(`/missions/${m._id}`)}
                showActions={false}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
